import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, getDocs, deleteDoc, doc, orderBy, updateDoc, runTransaction } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AdminOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const db = getFirestore();

  useEffect(() => {
    fetchAllOrders();
  }, []);

  const fetchAllOrders = async () => {
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, orderBy('createdAt', 'desc'));

    try {
      const querySnapshot = await getDocs(q);
      const allOrders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt ? doc.data().createdAt.toDate() : new Date()
      }));
      setOrders(allOrders);
    } catch (error) {
      console.error("Error fetching orders: ", error);
    }
  };

  const fulfillOrder = async (order) => {
    try {
      await runTransaction(db, async (transaction) => {
        console.log('Order being fulfilled:', JSON.stringify(order, null, 2));

        // First, perform all reads
        const orderRef = doc(db, 'orders', order.id);
        const productUpdates = [];

        for (const item of order.items) {
          const productId = item.id || item.productId;
          if (!productId) {
            console.warn(`No product ID found for item: ${JSON.stringify(item)}`);
            continue;
          }

          const productRef = doc(db, 'Products', productId);
          const productDoc = await transaction.get(productRef);

          if (!productDoc.exists()) {
            console.warn(`Product ${productId} not found in the database`);
            continue;
          }

          const currentAmount = productDoc.data().amount;
          if (typeof currentAmount !== 'number') {
            console.warn(`Invalid amount for product ${productId}: ${currentAmount}`);
            continue;
          }

          const newAmount = Math.max(currentAmount - item.quantity, 0);
          productUpdates.push({ ref: productRef, newAmount });
        }

        // Then, perform all writes
        transaction.update(orderRef, { status: 'fulfilled' });

        for (const update of productUpdates) {
          transaction.update(update.ref, { amount: update.newAmount });
        }
      });

      // Send email notification about fulfilled order
      const functions = getFunctions();
      const sendEmail = httpsCallable(functions, 'sendEmail');
      
      // Format items for email
      const itemsList = order.items.map(item => 
        `${item.name} x ${item.quantity} - $${(item.price * item.quantity).toFixed(2)}`
      ).join('<br>');
      
      // Send email to the customer
      if (order.userEmail) {
        await sendEmail({
          to: order.userEmail,
          subject: 'Your Order Has Been Fulfilled',
          html: `
            <h2>Your Order Has Been Fulfilled</h2>
            <p>Hello,</p>
            <p>We're pleased to inform you that your order has been fulfilled and is ready for pickup.</p>
            <h3>Order Details:</h3>
            <p><strong>Order ID:</strong> ${order.id}</p>
            <p><strong>Date Placed:</strong> ${new Date(order.createdAt).toLocaleString()}</p>
            <p><strong>Payment Method:</strong> ${order.paymentMethod || 'Not specified'}</p>
            <h3>Items:</h3>
            <p>${itemsList}</p>
            <p><strong>Total Amount:</strong> $${order.total.toFixed(2)}</p>
            <p>Thank you for your business!</p>
          `
        });
      }

      console.log(`Order ${order.id} fulfilled and inventory updated`);
      fetchAllOrders(); // Refresh the orders list
    } catch (error) {
      console.error("Error fulfilling order:", error);
      alert(`Failed to fulfill order: ${error.message}`);
    }
  };

  const handleRemoveOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to remove this order?')) {
      try {
        await deleteDoc(doc(db, 'orders', orderId));
        setOrders(orders.filter(order => order.id !== orderId));
        console.log("Order successfully removed!");
      } catch (error) {
        console.error("Failed to remove order:", error);
      }
    }
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'Date not available';

    try {
      if (dateInput instanceof Date) {
        return dateInput.toLocaleString();
      }
      if (typeof dateInput.toDate === 'function') {
        return dateInput.toDate().toLocaleString();
      }
      if (typeof dateInput === 'object' && dateInput.seconds) {
        const date = new Date(dateInput.seconds * 1000);
        return date.toLocaleString();
      }
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleString();
    } catch (error) {
      console.error("Error formatting date:", error);
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600';
      case 'fulfilled':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="container mx-auto px-4">
      <h2 className="text-2xl font-bold mb-6">Order Management</h2>
      {orders.length === 0 ? (
        <p className="text-gray-600">No orders found.</p>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {orders.map(order => (
            <div key={order.id} className="bg-white shadow-md rounded-lg p-6 transition duration-300 ease-in-out hover:shadow-lg">
              <p className="text-sm text-gray-600 mb-2">{formatDate(order.createdAt)}</p>
              <p className="font-semibold">Order ID: {order.id}</p>
              <p>User Email: {order.userEmail || 'N/A'}</p>
              <p>Total: ${order.total?.toFixed(2) || '0.00'}</p>
              <p className="mb-4">Status: <span className={`font-semibold ${getStatusColor(order.status)}`}>{order.status || 'pending'}</span></p>
              <h3 className="font-semibold mb-2">Items:</h3>
              <ul className="list-disc list-inside mb-4">
                {order.items?.map((item, index) => (
                  <li key={index} className="text-sm">
                    {item.name} - Quantity: {item.quantity}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                {order.status === 'pending' && (
                  <button
                    onClick={() => fulfillOrder(order)}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition duration-300 ease-in-out"
                  >
                    Fulfill Order
                  </button>
                )}
                <button
                  onClick={() => handleRemoveOrder(order.id)}
                  className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition duration-300 ease-in-out"
                >
                  Remove Order
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrderManagement;